import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Flex, Grid, Heading, Text } from '@chakra-ui/react';
import type { SiteMode, MediaItem, FormSubmission } from '@denise-augusta/domain';
import type { ContentStatus } from '@denise-augusta/capabilities';
import { executeAction } from '@/lattice/runtime';
import { usePortalUser, usePortalRole } from '@/auth/roles';
import { environment } from '@/config/environment';

export function Dashboard() {
  const user = usePortalUser();
  const role = usePortalRole();
  const [content, setContent] = useState<ContentStatus | null>(null);
  const [media, setMedia] = useState<MediaItem[] | null>(null);
  const [mode, setMode] = useState<SiteMode | null>(null);
  const [inquiries, setInquiries] = useState<FormSubmission[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (role !== 'admin') return;
    executeAction<ContentStatus>('site-content:getContentStatus', {}, user)
      .then(setContent)
      .catch((err: Error) => setError(err.message));
    executeAction<MediaItem[]>('media-library:listMedia', {}, user)
      .then(setMedia)
      .catch(() => setMedia([]));
    executeAction<{ mode: SiteMode }>('site-content:getSiteMode', {}, user)
      .then((result) => setMode(result.mode))
      .catch(() => setMode(null));
    executeAction<FormSubmission[]>('form-submissions:listSubmissions', {}, user)
      .then(setInquiries)
      .catch(() => setInquiries([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  const newInquiries = inquiries?.filter((item) => item.status === 'new').length ?? 0;

  // Client role: no features released yet.
  if (role !== 'admin') {
    return (
      <Box>
        <Heading fontFamily="heading" fontSize="2.2rem" fontWeight="300" color="brand.ink" mb="0.6rem">
          Welcome
        </Heading>
        <Text fontSize="0.9rem" color="ink.soft">
          No portal features have been released to your account yet.
        </Text>
      </Box>
    );
  }

  return (
    <Box>
      <Flex justify="space-between" align="flex-start" wrap="wrap" gap="1rem" mb="2rem">
        <Box>
          <Heading fontFamily="heading" fontSize="2.2rem" fontWeight="300" color="brand.ink" mb="0.4rem">
            Dashboard
          </Heading>
          <Text fontSize="0.9rem" color="ink.soft">
            Environment: <strong>{environment.name}</strong> · Storage:{' '}
            <strong>{environment.storage.blobEndpoint || 'not configured'}</strong>
          </Text>
        </Box>
        {mode && (
          <Box
            asChild
            textDecoration="none"
            px="1rem"
            py="0.5rem"
            borderRadius="20px"
            fontSize="0.68rem"
            fontWeight="600"
            letterSpacing="0.15em"
            textTransform="uppercase"
            bg={mode === 'live' ? 'brand.wash' : '#FBEDEB'}
            color={mode === 'live' ? 'brand.mid' : 'brand.terracotta'}
            border="1px solid"
            borderColor={mode === 'live' ? 'brand.pale' : 'brand.terracotta'}
          >
            <RouterLink to="/portal/settings" title="Change in Settings">
              {mode === 'live' ? '🌐 Site is Live' : '🔒 Site is Locked'}
            </RouterLink>
          </Box>
        )}
      </Flex>

      {error && (
        <Box bg="#FBEDEB" border="1px solid" borderColor="brand.terracotta" borderRadius="12px" px="1.4rem" py="1rem" mb="1.5rem">
          <Text fontSize="0.85rem" color="brand.terracotta">
            {error}
          </Text>
        </Box>
      )}

      <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap="1.5rem">
        <Card
          title={
            newInquiries > 0
              ? `Inquiries · ${newInquiries} new`
              : 'Inquiries'
          }
          body={
            inquiries === null
              ? 'Loading…'
              : inquiries.length === 0
                ? 'No one has used the contact form yet.'
                : newInquiries > 0
                  ? `${newInquiries} waiting on you, ${inquiries.length} in total.`
                  : `All caught up · ${inquiries.length} in total.`
          }
          links={[{ label: 'Open inbox →', to: '/portal/inquiries' }]}
          highlight={newInquiries > 0}
        />
        <Card
          title="Site Content"
          body={
            content
              ? `Live version ${content.publishedVersion} · ${
                  content.hasDraft
                    ? `draft in progress (${content.draftUpdatedAt ? new Date(content.draftUpdatedAt).toLocaleString() : ''})`
                    : 'no draft'
                }`
              : 'Loading…'
          }
          links={[{ label: 'Edit content →', to: '/portal/content' }]}
        />
        <Card
          title="Media Library"
          body={media ? `${media.length} file${media.length === 1 ? '' : 's'} in storage` : 'Loading…'}
          links={[{ label: 'Manage media →', to: '/portal/media' }]}
        />
        <Card
          title="Settings"
          body="Site mode (locked / live) and AI assist configuration."
          links={[{ label: 'Open settings →', to: '/portal/settings' }]}
        />
        <Card
          title="Public Site"
          body={
            content?.hasDraft
              ? 'View the live site, or preview the working draft before promoting it.'
              : 'Open the site as visitors see it.'
          }
          links={[
            // Full page loads — preview mode resolves at app boot, so these
            // must not be client-side router navigations.
            { label: 'View site →', to: '/?preview=live', hard: true },
            ...(content?.hasDraft ? [{ label: 'View draft →', to: '/?preview=draft', hard: true }] : []),
          ]}
        />
      </Grid>
    </Box>
  );
}

function Card({
  title,
  body,
  links,
  highlight = false,
}: {
  title: string;
  body: string;
  links: Array<{ label: string; to: string; hard?: boolean }>;
  highlight?: boolean;
}) {
  return (
    <Box
      bg="paper.white"
      border="1px solid"
      borderColor={highlight ? 'brand.terracotta' : 'paper.border'}
      borderRadius="16px"
      p="1.8rem"
    >
      <Heading as="h3" fontFamily="heading" fontSize="1.3rem" fontWeight="400" color="brand.ink" mb="0.5rem">
        {title}
      </Heading>
      <Text fontSize="0.85rem" color="ink.soft" mb="1.2rem">
        {body}
      </Text>
      <Flex gap="1.4rem" wrap="wrap">
        {links.map((link) => (
          <Box
            key={link.to}
            asChild
            fontSize="0.78rem"
            fontWeight="500"
            letterSpacing="0.08em"
            textTransform="uppercase"
            color="brand.mid"
            textDecoration="none"
            _hover={{ color: 'brand.terracotta' }}
          >
            {link.hard ? (
              <a href={link.to}>{link.label}</a>
            ) : (
              <RouterLink to={link.to}>{link.label}</RouterLink>
            )}
          </Box>
        ))}
      </Flex>
    </Box>
  );
}
